import { useState } from 'react';

import { useDebounce } from 'use-debounce';

export interface UseSearchInputOptions {
  /** Debounce delay in milliseconds (default: 200) */
  debounceMs?: number;
  /** Initial search value (default: empty string) */
  initialValue?: string;
}

/**
 * Manages search input state with debouncing.
 *
 * Returns both immediate and debounced values for responsive UI
 * with delayed search execution.
 *
 * @example
 * ```tsx
 * const { searchInput, setSearchInput, debouncedSearchInput } = useSearchInput();
 *
 * <OutlinedInput
 *   value={searchInput}
 *   onChange={(e) => setSearchInput(e.target.value)}
 * />
 *
 * // Use debouncedSearchInput for API calls
 * useSearchResults({ query: debouncedSearchInput });
 * ```
 */
export const useSearchInput = (options: UseSearchInputOptions = {}) => {
  const { debounceMs = 200, initialValue = '' } = options;
  const [searchInput, setSearchInput] = useState(initialValue);
  const [debouncedSearchInput] = useDebounce(searchInput, debounceMs);

  return { searchInput, setSearchInput, debouncedSearchInput };
};
